import { useEffect, useState } from 'react';
import type { PanelOrganizationDetailDto } from '@icebreaker/shared';
import { Avatar, Button, Tag, useErrorMessage } from '@icebreaker/ui';
import { numericDateTimeOptions } from '@/features/sales/model/sales-view';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { ownerView, type OwnerViewKind } from '../model/owner-view';
import { isOutcomeUnknown } from '../model/write-outcome';
import { ownerActionReset, resendOwnerInvitation } from '../store/organizations.slice';
import { OwnerInvitationDialog } from './owner-invitation-dialog';
import styles from './organizations.module.css';

const STEP_KEYS = ['sent', 'accepted', 'declined', 'expired'] as const;
type StepKey = (typeof STEP_KEYS)[number];

const CURRENT_STEP: Partial<Record<OwnerViewKind, StepKey>> = {
  invited: 'sent',
  expired: 'expired',
  declined: 'declined',
};

const STEP_LABEL: Record<StepKey, 'stepSent' | 'stepAccepted' | 'stepDeclined' | 'stepExpired'> = {
  sent: 'stepSent',
  accepted: 'stepAccepted',
  declined: 'stepDeclined',
  expired: 'stepExpired',
};

/**
 * SAHİP (P5 bölüm 2): etkin sahip (48 px avatar + ad + e-posta) ya da sahip
 * daveti hâli ve eylemleri. Davet adımları sunucunun izlediği durumlardır:
 * Gönderildi · Kabul · Red · Süresi doldu. Referanstaki "Açıldı" adımı
 * veritabanında izlenmediği için ÇİZİLMEZ (uydurma veri yok).
 */
export function OrganizationOwnerCard({ org }: { org: PanelOrganizationDetailDto }) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const errorMessage = useErrorMessage();
  const action = useAppSelector((root: RootState) => root.panelOrgs.ownerAction);
  const [dialog, setDialog] = useState<'replace' | 'invite' | null>(null);
  const owner = ownerView(org.owner);
  const dateTime = numericDateTimeOptions(t.locale);

  useEffect(() => {
    dispatch(ownerActionReset());
  }, [dispatch, org.id]);

  const resending = action.kind === 'resend' && action.phase === 'loading';
  const pending = owner.kind === 'invited' || owner.kind === 'expired';
  const kicker =
    owner.kind === 'invited'
      ? t.t('admin.orgs.detail.ownerPendingKicker')
      : owner.kind === 'expired'
        ? t.t('admin.orgs.detail.ownerExpiredKicker')
        : owner.kind === 'declined'
          ? t.t('admin.orgs.detail.ownerDeclinedKicker')
          : t.t('admin.orgs.detail.ownerNoneKicker');
  const current = CURRENT_STEP[owner.kind];

  return (
    <div className={styles.card}>
      <h2 className={styles.cardTitle}>{t.t('admin.orgs.detail.ownerTitle')}</h2>
      {owner.kind === 'active' ? (
        <div className={styles.owner}>
          <Avatar name={owner.name ?? owner.email ?? '—'} size="lg" />
          <span className={styles.ownerText}>
            <span className={styles.ownerName}>
              {owner.name ?? t.t('admin.orgs.owner.removedAccount')}
            </span>
            {owner.email ? <span className={styles.ownerEmail}>{owner.email}</span> : null}
          </span>
          <Tag variant="success">{t.t('admin.orgs.detail.ownerActive')}</Tag>
        </div>
      ) : (
        <div className={styles.inviteBox}>
          <p className={styles.inviteKicker}>{kicker}</p>
          {current ? (
            <ol className={styles.steps} aria-label={t.t('admin.orgs.detail.ownerStepsLabel')}>
              {STEP_KEYS.map((step) => (
                <li
                  key={step}
                  className={[styles.step, step === current ? styles.stepCurrent : '']
                    .filter(Boolean)
                    .join(' ')}
                  aria-current={step === current ? 'step' : undefined}
                >
                  {t.t(`admin.orgs.detail.${STEP_LABEL[step]}`)}
                </li>
              ))}
            </ol>
          ) : null}
          {owner.email ? (
            <p className={styles.inviteMeta}>
              <span>
                {t.t('admin.orgs.detail.invitedTo')} ·{' '}
                <span className={styles.mono}>{owner.email}</span>
              </span>
            </p>
          ) : null}
          {org.owner && (org.owner.invitationSentAt || org.owner.invitationExpiresAt) ? (
            <p className={styles.inviteMeta}>
              {org.owner.invitationSentAt ? (
                <span>
                  {t.t('admin.orgs.detail.sentAt', {
                    date: t.formatDate(org.owner.invitationSentAt, dateTime),
                  })}
                </span>
              ) : null}
              {org.owner.invitationExpiresAt ? (
                <span>
                  {t.t('admin.orgs.detail.expiresAt', {
                    date: t.formatDate(org.owner.invitationExpiresAt, dateTime),
                  })}
                </span>
              ) : null}
            </p>
          ) : null}
          <div className={styles.inviteActions}>
            {pending ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={resending}
                  disabled={action.phase === 'loading'}
                  onClick={() =>
                    dispatch(resendOwnerInvitation.request({ organizationId: org.id }))
                  }
                >
                  {t.t('admin.orgs.detail.resend')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={action.phase === 'loading'}
                  onClick={() => setDialog('replace')}
                >
                  {t.t('admin.orgs.detail.replace')}
                </Button>
              </>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                disabled={action.phase === 'loading'}
                onClick={() => setDialog('invite')}
              >
                {t.t('admin.orgs.detail.inviteNew')}
              </Button>
            )}
          </div>
        </div>
      )}
      {action.notice && dialog === null ? (
        <p className={styles.notice} role="status">
          {action.notice === 'resent'
            ? t.t('admin.orgs.detail.resent')
            : t.t('admin.orgs.detail.replaced')}
        </p>
      ) : null}
      {action.kind === 'resend' && action.phase === 'failed' ? (
        <p
          className={[styles.notice, styles.noticeError].join(' ')}
          role="alert"
          data-outcome={isOutcomeUnknown(action.error) ? 'unknown' : 'rejected'}
        >
          {isOutcomeUnknown(action.error)
            ? `${t.t('admin.write.unknown')} ${t.t('admin.write.reloadedOwner')}`
            : (errorMessage(action.error) ?? t.t('admin.orgs.detail.actionError'))}
        </p>
      ) : null}
      {dialog ? (
        <OwnerInvitationDialog
          organizationId={org.id}
          organizationName={org.name}
          currentEmail={owner.email}
          revokeFirst={dialog === 'replace'}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}
